"use client";

import * as React from "react";

import { cn } from "@/utils";

/**
 * ChatBubble — a conversational message row. basis extension (no canonical shadcn component).
 *
 * `variant` distinguishes the two sides of a conversation and flows to the sub-parts via context,
 * so you set it once on the row:
 *   - received → the other party (an assistant / an incoming message): left-aligned, muted surface.
 *   - sent     → the current user (an outgoing message): right-aligned, primary surface.
 *
 * Compose:
 *   <ChatBubble variant="received">
 *     <ChatBubbleAvatar tone="primary"><Bot /></ChatBubbleAvatar>
 *     <ChatBubbleContent>
 *       <ChatBubbleMessage>…</ChatBubbleMessage>
 *       <ChatBubbleMeta>2:31 PM · AI</ChatBubbleMeta>
 *     </ChatBubbleContent>
 *   </ChatBubble>
 *
 * Place the avatar before the content for `received` and after it for `sent` (or omit it entirely).
 * Footer chrome — timestamps, an AI-disclosure label, a source link, feedback controls — goes in
 * <ChatBubbleMeta>, whose locked height keeps hover-revealed controls from shifting later messages.
 */
type ChatBubbleVariant = "sent" | "received";

const ChatBubbleContext = React.createContext<ChatBubbleVariant>("received");
const useChatBubbleVariant = () => React.useContext(ChatBubbleContext);

function ChatBubble({
  className,
  variant = "received",
  ...props
}: React.ComponentProps<"div"> & { variant?: ChatBubbleVariant }) {
  return (
    <ChatBubbleContext.Provider value={variant}>
      <div
        data-slot="chat-bubble"
        data-variant={variant}
        className={cn(
          "group/chat-bubble flex items-start gap-2",
          variant === "sent" && "justify-end",
          className,
        )}
        {...props}
      />
    </ChatBubbleContext.Provider>
  );
}

/**
 * ChatBubbleAvatar — small round avatar for a message row. Pass the glyph (an icon, initials) as
 * children. Color tracks *who* is speaking, not which side: `tone="primary"` for the agent/bot,
 * the default muted tone for a human. `accent` overrides with an arbitrary brand color (e.g. a
 * per-agent color) for provenance.
 */
function ChatBubbleAvatar({
  className,
  tone = "muted",
  accent,
  style,
  ...props
}: React.ComponentProps<"div"> & { tone?: "muted" | "primary"; accent?: string }) {
  return (
    <div
      data-slot="chat-bubble-avatar"
      className={cn(
        "flex size-7 shrink-0 items-center justify-center rounded-full [&_svg]:size-4",
        !accent && (tone === "primary" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"),
        className,
      )}
      style={accent ? { backgroundColor: `${accent}26`, color: accent, ...style } : style}
      {...props}
    />
  );
}

/** ChatBubbleContent — the vertical column beside the avatar holding the message and its meta. */
function ChatBubbleContent({
  className,
  variant: variantProp,
  ...props
}: React.ComponentProps<"div"> & { variant?: ChatBubbleVariant }) {
  const ctxVariant = useChatBubbleVariant();
  const variant = variantProp ?? ctxVariant;
  return (
    <div
      data-slot="chat-bubble-content"
      className={cn(
        "flex min-w-0 flex-col gap-1",
        variant === "sent" ? "max-w-[80%] items-end" : "flex-1",
        className,
      )}
      {...props}
    />
  );
}

/**
 * ChatBubbleMessage — the rounded message surface. `loading` renders an animated typing indicator
 * in place of children.
 */
function ChatBubbleMessage({
  className,
  variant: variantProp,
  loading = false,
  children,
  ...props
}: React.ComponentProps<"div"> & { variant?: ChatBubbleVariant; loading?: boolean }) {
  const ctxVariant = useChatBubbleVariant();
  const variant = variantProp ?? ctxVariant;
  return (
    <div
      data-slot="chat-bubble-message"
      className={cn(
        "w-fit rounded-lg px-3 py-2 text-sm",
        variant === "sent"
          ? "rounded-br-sm bg-primary text-primary-foreground"
          : "max-w-[85%] rounded-bl-sm bg-muted",
        className,
      )}
      {...props}
    >
      {loading ? <TypingDots /> : children}
    </div>
  );
}

function TypingDots() {
  return (
    <span className="flex items-center gap-1 py-1" role="status" aria-label="Typing">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="size-1.5 animate-bounce rounded-full bg-muted-foreground/50"
          style={{ animationDelay: `${i * 120}ms` }}
        />
      ))}
    </span>
  );
}

/**
 * ChatBubbleMeta — fixed-height (h-6) footer row under a message for small chrome: a timestamp, an
 * AI-disclosure label, a source link, feedback controls. The locked height means controls revealed
 * on hover (e.g. an opacity transition) never shift the messages below.
 */
function ChatBubbleMeta({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="chat-bubble-meta"
      className={cn(
        "flex h-6 items-center gap-2 px-1 text-[10px] text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

export {
  ChatBubble,
  ChatBubbleAvatar,
  ChatBubbleContent,
  ChatBubbleMessage,
  ChatBubbleMeta,
};
